import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, User, Menu, X, MessageCircle, Mail, Settings } from 'lucide-react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'lawyer') return '/lawyer/dashboard';
    return '/dashboard';
  };

  return (
    <nav className="sticky top-0 z-50 glass border-b border-slate-200">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="text-xl font-bold text-slate-900" data-testid="nav-logo">
            Legal<span className="text-amber-600">Hub</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/lawyers" 
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-find-lawyers"
            >
              Find Lawyers
            </Link>
            <Link 
              to="/about" 
              className="text-slate-600 hover:text-slate-900 font-medium"
              data-testid="nav-about-link"
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className="text-slate-600 hover:text-slate-900 font-medium"
              data-testid="nav-contact-link"
            >
              Contact
            </Link>
            
            {!user ? (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" data-testid="nav-login-btn">Login</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-slate-900 hover:bg-slate-800 rounded-full" data-testid="nav-register-btn">
                    Get Started
                  </Button>
                </Link>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    data-testid="nav-user-menu"
                  >
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="font-medium text-slate-700">{user.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="w-full" data-testid="nav-dashboard-link">
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {user.role === 'lawyer' && (
                    <DropdownMenuItem asChild>
                      <Link to="/lawyer/profile/settings" className="w-full flex items-center gap-2" data-testid="nav-edit-profile-link">
                        <Settings className="w-4 h-4" />
                        Edit Profile
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {(user.role === 'client' || user.role === 'lawyer') && (
                    <DropdownMenuItem asChild>
                      <Link to="/messages" className="w-full flex items-center gap-2" data-testid="nav-messages-link">
                        <MessageCircle className="w-4 h-4" />
                        Messages
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/emails" className="w-full flex items-center gap-2" data-testid="nav-emails-link">
                        <Mail className="w-4 h-4" />
                        Emails
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="nav-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="nav-mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              <Link 
                to="/lawyers" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Find Lawyers
              </Link>
              <Link 
                to="/about" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </Link>
              
              {!user ? (
                <>
                  <Link 
                    to="/login"
                    className="text-slate-600 hover:text-slate-900 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className="w-full bg-slate-900 hover:bg-slate-800 rounded-full">
                      Get Started
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link 
                    to={getDashboardLink()}
                    className="text-slate-600 hover:text-slate-900 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  {user.role === 'lawyer' && (
                    <Link 
                      to="/lawyer/profile/settings"
                      className="text-slate-600 hover:text-slate-900 font-medium flex items-center gap-2"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-nav-edit-profile-link"
                    >
                      <Settings className="w-4 h-4" />
                      Edit Profile
                    </Link>
                  )}
                  {(user.role === 'client' || user.role === 'lawyer') && (
                    <Link 
                      to="/messages"
                      className="text-slate-600 hover:text-slate-900 font-medium flex items-center gap-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <MessageCircle className="w-4 h-4" />
                      Messages
                    </Link>
                  )}
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="text-red-600 font-medium text-left"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
